import { ReactNode, useCallback, useState } from 'react';
import {
  Block as NoAccessIcon,
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
  Inbox as NoDataIcon,
  SearchOff as NotFoundIcon,
} from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import Box from '@mui/system/Box';
import { useNavigate } from 'react-router-dom';

import { PageLayout } from '@/components/PageLayout';
import '@/pages/nopermission/style.scss';

import { type DataState, dataState } from './accessAnswer';

export { accessAnswer, dataState, isEmptyData } from './accessAnswer';
export type { AccessAnswer, DataState } from './accessAnswer';

/**
 * The one way a chart, list or page says "no access", "not found" or "no
 * data" (Authorization part 8a, item 6; plan §15). The texts never name what
 * exists outside the user's scope: "not found" is the same for a record that
 * does not exist and one the scope does not cover, as the server answers.
 */
export const ACCESS_STATE_TEXT: Record<
  Exclude<DataState, 'ok'>,
  { title: string; text: string }
> = {
  no_access: {
    title: 'No access',
    text: 'You do not have access to this information.',
  },
  not_found: {
    title: 'Not found',
    text: 'This page does not exist or is not available to you.',
  },
  no_data: {
    title: 'No data found',
    text: 'No data is available within your current scope and filters.',
  },
  error: {
    title: 'Something went wrong',
    text: 'The data could not be loaded. Please try again.',
  },
};

const ICONS = {
  no_access: NoAccessIcon,
  not_found: NotFoundIcon,
  no_data: NoDataIcon,
  error: ErrorIcon,
} as const;

type AccessStateProps = {
  state: DataState;
  /** Shown for `ok` (the chart / list itself). */
  children?: ReactNode;
  /** Replaces the default sentence (e.g. the §15 text naming the partner). */
  text?: string;
};

/** Inside a chart or list card: the state as a short centred text; `ok` renders the children. */
export function AccessState({ state, children = null, text }: AccessStateProps) {
  if (state === 'ok') return <>{children}</>;
  const copy = ACCESS_STATE_TEXT[state];
  const Icon = ICONS[state];
  return (
    <Box
      data-testid={`access-state-${state}`}
      role='status'
      sx={{ textAlign: 'center', padding: '16px 8px', color: 'text.secondary' }}
    >
      <Icon sx={{ fontSize: '32px', opacity: 0.7 }} />
      <Typography align='center' sx={{ fontWeight: 600 }}>
        {copy.title}
      </Typography>
      <Typography align='center' variant='body2'>
        {text ?? copy.text}
      </Typography>
    </Box>
  );
}

type AccessStatePageProps = {
  state: Exclude<DataState, 'ok'>;
  text?: string;
  /** false: without the app shell (the caller already renders PageLayout). */
  isPage?: boolean;
};

/** A whole page in that state (a profile outside the scope: "Not found"), with the way home. */
export function AccessStatePage({ state, text, isPage = true }: AccessStatePageProps) {
  const navigate = useNavigate();
  const copy = ACCESS_STATE_TEXT[state];
  const Icon = ICONS[state];
  const content = (
    <div
      className={
        isPage
          ? 'modern-form-page modern-view-page no-permission-page'
          : 'no-permission-page no-permission-embedded'
      }
      data-testid={`access-state-page-${state}`}
    >
      <div className='progress_container'>
        <div className='work_progress no-permission-card'>
          <Icon sx={{ fontSize: '96px' }} />
          <Typography variant='body1' className='margin_b'>
            {copy.title.toUpperCase()}
          </Typography>
          <Typography variant='body2'>{text ?? copy.text}</Typography>
          <div className='margin_top2 text_center'>
            <Button
              variant='contained'
              color='secondary'
              startIcon={<HomeIcon />}
              sx={{ margin: '0 auto' }}
              onClick={() => navigate('/dashboard')}
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  return isPage ? <PageLayout>{content}</PageLayout> : <Box>{content}</Box>;
}

/**
 * State of one request for a chart / list: `[state, track, reset]`.
 * `track(response, isEmpty?)` reads the server answer (also a rejected
 * request: pass the error) and returns the state; `reset()` goes back to
 * `ok` before the next request.
 */
export function useDataState(initial: DataState = 'ok') {
  const [state, setState] = useState<DataState>(initial);
  const track = useCallback(
    // eslint-disable-next-line no-unused-vars
    (response: unknown, isEmpty?: (data: unknown) => boolean): DataState => {
      const next = dataState(response, isEmpty);
      setState(next);
      return next;
    },
    [],
  );
  const reset = useCallback(() => setState('ok'), []);
  return [state, track, reset] as const;
}
